// src/hooks/useChatSocket.js
// Phase 7 update:
//   • message.edited now patches `content` field (not `body`)
//   • reaction.added / reaction.removed events handled
//   • message.seen / message.delivered stubs added
//   • token.expired → refresh + reconnect
//   • FIXED: presence.update now calls store.setPresence() (not updatePresence())
//   • FIXED: setCurrentUser() seeded from localStorage on hook mount

import { useEffect, useRef, useCallback } from "react";
import useAuthStore from "../store/authStore";
import useChatStore, { toKey } from "../store/chatStore";
import apiClient, { setLoggingOut } from "../api/apiClient";

const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];
const getWsBase = () => {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
};
const WS_BASE = getWsBase();

export function useChatSocket() {
  const ws             = useRef(null);
  const attempt        = useRef(0);
  const pingTimer      = useRef(null);
  const reconnectTimer = useRef(null);

  const store = useChatStore.getState();

  const handleEvent = useCallback((event) => {
    if (event && event.type && event.type.startsWith("support.")) {
      window.dispatchEvent(new CustomEvent(event.type, { detail: event }));

      if (event.type === "support.message.new") {
        const state = useChatStore.getState();
        const myId = state.currentUserId;
        const isOwn = event.message?.sender_id === myId;
        if (!isOwn) {
          try {
            const audio = new Audio("/sounds/notification.mp3");
            audio.play().catch(e => console.warn("Audio play blocked", e));
          } catch (e) {
            // ignore
          }

          if (document.visibilityState !== 'visible' && 'Notification' in window && Notification.permission === 'granted') {
            const senderName = event.message?.sender_name || 'Support';
            const content = event.message?.content || 'Sent a message';
            const icon = event.message?.sender_photo || '/favicon.ico';
            
            const notif = new Notification(`Support: ${senderName}`, {
              body: content,
              icon: icon,
              requireInteraction: true,
              tag: `support-conv-${event.conversation_id}`
            });
            notif.onclick = () => {
              window.focus();
              const role = (useAuthStore.getState().role || 'EMPLOYEE').toLowerCase();
              window.location.href = `/${role}/profile?support=open`;
              notif.close();
            };
          }
        }
      }
    }

    switch (event.type) {

      // ── New message ──────────────────────────────────────────────────────
      case "message.new": {
        const { message } = event;
        const convId = message.conversation;

        const state = useChatStore.getState();
        const myId = state.currentUserId;
        const isOwn = message.sender?.employee_id === myId || message.sender_employee_id === myId || message.sender_id === myId;

        message.is_read_by_me = isOwn;
        store.appendMessage(convId, message);

        // If not the currently selected conversation, bump unread count
        if (!isOwn) {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: "message_delivered",
              message_id: message.id,
              conv_id: convId
            }));
          }
        }

        if (toKey(state.selectedConvId) !== toKey(convId) && !isOwn) {
          store.incrementUnread(convId);
        }

        const conv = state.conversations.find((c) => toKey(c.id) === toKey(convId));
        const isMuted = conv?.is_muted === true;

        console.log("[useChatSocket] message.new audio logic:", {
          convId,
          myId,
          isOwn,
          isMuted,
          convFound: !!conv,
          convMutedStatus: conv?.is_muted
        });

        // Play notification sound for all incoming messages from others if not muted
        if (!isOwn && !isMuted) {
          try {
            const audio = new Audio("/sounds/notification.mp3");
            audio.play().catch(e => console.warn("Audio play blocked", e));
          } catch (e) {
            // ignore
          }
        }

        // Bubble last_message preview up to conversation list
        store.upsertConversation({ id: convId, last_message: message });
        break;
      }

      // ── Edited message ───────────────────────────────────────────────────
      case "message.edited": {
        const { message } = event;
        const convId = message?.conversation ?? event.conv_id;
        const msgId  = message?.id           ?? event.message_id;

        if (convId != null && msgId != null) {
          store.updateMessage(convId, msgId, message ?? {
            content:    event.content,
            ciphertext: event.ciphertext,
            iv:         event.iv,
            signature:  event.signature,
            is_edited:  true,
            edited_at:  event.edited_at,
          });
        } else if (msgId != null) {
          // Fallback: flat event shape from views.py broadcast without convId
          const state = useChatStore.getState();
          Object.keys(state.messages).forEach((cid) => {
            const exists = (state.messages[cid] || []).some(
              (m) => String(m.id) === String(msgId)
            );
            if (exists) {
              store.updateMessage(Number(cid), msgId, {
                content:    event.content,
                ciphertext: event.ciphertext,
                iv:         event.iv,
                signature:  event.signature,
                is_edited:  true,
                edited_at:  event.edited_at,
              });
            }
          });
        }
        break;
      }

      // ── Deleted message ──────────────────────────────────────────────────
      case "message.deleted": {
        const msgId      = event.message_id ?? event.id;
        const convId     = event.conv_id ?? event.conversation;
        const deleteType = event.delete_type ?? "everyone";

        if (convId != null && msgId != null) {
          store.deleteMessage(convId, msgId, deleteType);
        } else {
          // Search all conversations
          const state = useChatStore.getState();
          Object.keys(state.messages).forEach((cid) => {
            const exists = (state.messages[cid] || []).some((m) => m.id === msgId);
            if (exists) store.deleteMessage(Number(cid), msgId, deleteType);
          });
        }
        break;
      }

      // ── Reactions ────────────────────────────────────────────────────────
      case "reaction.added":
      case "reaction.removed": {
        const { message_id, reaction_counts, conv_id } = event;
        if (message_id != null && reaction_counts != null) {
          if (conv_id != null) {
            store.updateMessage(conv_id, message_id, { reactions: reaction_counts });
          } else {
            // Search all conversations
            const state = useChatStore.getState();
            Object.keys(state.messages).forEach((cid) => {
              const exists = (state.messages[cid] || []).some((m) => m.id === message_id);
              if (exists) {
                store.updateMessage(Number(cid), message_id, { reactions: reaction_counts });
              }
            });
          }
        }
        break;
      }

      // ── Typing ───────────────────────────────────────────────────────────
      case "typing.start":
        store.setTyping(event.conv_id, event.user_id, event.full_name);
        break;

      case "typing.stop":
        store.clearTyping(event.conv_id, event.user_id);
        break;

      // ── Presence ─────────────────────────────────────────────────────────
      case "presence.update":
        // FIXED: store has setPresence(employeeId, status), not updatePresence()
        if (event.user_id) {
          store.setPresence(event.user_id, event.status ?? "OFFLINE");
        }
        break;

      // ── Token expiry ──────────────────────────────────────────────────────
      case "token.expired":
        handleTokenExpiry();
        break;

      // ── Heartbeat ─────────────────────────────────────────────────────────
      case "pong":
        break;

      // ── Read receipts ────────────────────────────────────────────────────
      case "message.seen": {
        const { conv_id, message_ids, reader_id, user_id } = event;
        const reader = reader_id || user_id;
        if (conv_id && message_ids && Array.isArray(message_ids)) {
          const state = useChatStore.getState();
          const myId = state.currentUserId;
          if (reader === myId) {
            message_ids.forEach((msgId) => {
              store.updateMessage(conv_id, msgId, { is_read_by_me: true });
            });
          } else {
            message_ids.forEach((msgId) => {
              store.updateMessage(conv_id, msgId, { is_read: true });
            });
          }
        }
        break;
      }
      case "message.delivered": {
        const { conv_id, message_id, user_id } = event;
        if (conv_id && message_id) {
          const state = useChatStore.getState();
          // Only update if someone ELSE received our message
          if (user_id !== state.currentUserId) {
            store.updateMessage(conv_id, message_id, { is_delivered: true });
          }
        }
        break;
      }

      // ── Conversation / member updates ─────────────────────────────────────
      case "conversation.updated":
        if (event.conv_id) {
          store.upsertConversation({ id: event.conv_id, ...event.changes });
        }
        break;

      case "member.added":
      case "member.removed":
        // Handled by re-fetching conversation detail when user opens it
        break;

      case "error":
        console.error("[WebSocket Error]", event);
        alert(`Failed to send message: ${event.message || "Unknown error"}`);
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Connect ─────────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    // Must be logged in (access_token placeholder set after login)
    const token = useAuthStore.getState().token;
    if (!token) return;

    // Pre-fetch initial unread counts and conversations list from API
    (async () => {
      try {
        const res = await apiClient.get("/chat/api/messages/unread-counts/");
        if (res.data?.unread_counts) {
          useChatStore.getState().setUnreadCounts(res.data.unread_counts);
          console.log("[useChatSocket] Pre-fetched unread counts successfully:", res.data.unread_counts);
        }
      } catch (err) {
        console.warn("Failed to pre-fetch unread counts:", err);
      }
      try {
        const res = await apiClient.get("/chat/api/conversations/");
        if (res.data) {
          useChatStore.getState().setConversations(res.data);
          console.log("[useChatSocket] Pre-fetched conversations list successfully, count:", res.data.length);
        }
      } catch (err) {
        console.warn("Failed to pre-fetch conversations:", err);
      }
    })();

    // Clean up existing socket
    if (ws.current) {
      ws.current.onclose = null;
      ws.current.close();
    }

    // Fetch a short-lived WS ticket first (cookie-based HTTP works fine),
    // then use the ticket in the WebSocket URL so the server can authenticate
    // without relying on cookies in the WS upgrade request.
    (async () => {
      let wsUrl = `${WS_BASE}/ws/chat/`;
      try {
        const ticketRes = await apiClient.get(`/accounts/api/ws-ticket/?_t=${Date.now()}`);
        if (ticketRes.data?.ticket) {
          wsUrl = `${WS_BASE}/ws/chat/?ws_token=${ticketRes.data.ticket}`;
          console.log("[useChatSocket] WS ticket obtained, connecting…");
        }
      } catch (err) {
        console.warn("[useChatSocket] Could not obtain WS ticket:", err);
        // Fall back to token-in-query-string (may fail if cookies not sent)
        wsUrl = `${WS_BASE}/ws/chat/?token=${token}`;
      }

      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        attempt.current = 0;
        useChatStore.getState().setWsConnected(true);

        // Send initial presence update
        ws.current.send(JSON.stringify({ 
          type: "presence_update", 
          status: "ONLINE" 
        }));

        // Heartbeat every 30 s
        pingTimer.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: "ping", id: Date.now() }));
          }
        }, 30000);
      };

      ws.current.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          handleEvent(event);
        } catch (err) {
          console.error("WS parse error:", err);
        }
      };

      ws.current.onclose = (e) => {
        clearInterval(pingTimer.current);
        useChatStore.getState().setWsConnected(false);

        // 4001 = unauthenticated, 4002 = inactive — don't retry
        if (e.code === 4001 || e.code === 4002) return;

        const delay = BACKOFF[Math.min(attempt.current, BACKOFF.length - 1)];
        attempt.current += 1;
        useChatStore.getState().setWsReconnecting(true);
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.current.onerror = () => ws.current?.close();
    })();
  }, [handleEvent]);


  useEffect(() => {
    // Seed currentUserId into the store from localStorage so all
    // components that read s.currentUserId get the right value immediately.
    try {
      const u = useAuthStore.getState().user;
      const savedEid = useAuthStore.getState().employeeId;
      const savedName = useAuthStore.getState().fullName;

      if (u) {
        const eid = u.employee_id || u.id || savedEid;
        const name = u.full_name || savedName;
        if (eid) useChatStore.getState().setCurrentUser(eid, name);
      } else if (savedEid) {
        useChatStore.getState().setCurrentUser(savedEid, savedName);
      }
    } catch { /* ignore */ }

    connect();
    return () => {
      clearInterval(pingTimer.current);
      clearTimeout(reconnectTimer.current);
      if (ws.current) {
        ws.current.onclose = null;
        ws.current.close();
      }
    };
  }, [connect]);

  // ── Public send ──────────────────────────────────────────────────────────────
  const send = useCallback((payload) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
    }
  }, []);

  useEffect(() => {
    useChatStore.getState().setSendWebSocketMessage(send);
  }, [send]);


  return {
    send,
    connected:    useChatStore.getState().wsConnected,
    reconnecting: useChatStore.getState().wsReconnecting,
  };
}

// ── Token refresh helper ──────────────────────────────────────────────────────
async function handleTokenExpiry() {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/accounts/api/token/refresh/`,
      {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );
    if (!res.ok) throw new Error("refresh failed");
    localStorage.setItem("has_session", "present");
    // onclose handler will trigger reconnect automatically
    useChatStore.getState().setWsConnected(false);
  } catch (err) {
    console.error("WS handleTokenExpiry failed:", err);
    setLoggingOut();
    localStorage.clear();
    window.location.href = "/login";
  }
}
